import { useFragment } from '@apollo/client';
import { TimelineEvents } from '@ps/isp-web-component-library';
import type { FootballPeriod, FootballPeriodStatus } from '@ps/web-shared-generated-graphql/sports';
import classNames from 'clsx';
import React, { useMemo } from 'react';

import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import { getEventScoreboardFragment } from '@swf/sports/utils/getEventScoreboardFragment';
import type { LiveData } from '@swf/sports/utils/getLiveData';
import { getIsFootballLiveData } from '@swf/sports/utils/getLiveData';

import styles from './MatchIncidents.scss';
import { mapIncidentsToMatchHalves } from './utils';

interface MatchIncidentsProps {
  eventId: number;
  eventTypeId: number;
}

export const MatchIncidents = (props: MatchIncidentsProps) => {
  const { eventId, eventTypeId } = props;
  const { variables } = useLiveData();
  const options = getEventScoreboardFragment(eventTypeId, eventId, variables);
  const { data } = useFragment<LiveData>(options);
  const footballData = getIsFootballLiveData(eventTypeId, data) ? data : null;

  const { firstHalf, secondHalf } = useMemo(
    () =>
      mapIncidentsToMatchHalves({
        incidents: footballData?.incidents || [],
        duration: footballData?.duration || undefined
      }),
    [footballData?.incidents, footballData?.duration]
  );

  const isValidFootballDurationStatus = (status: unknown): status is FootballPeriodStatus => {
    const validStatuses: FootballPeriodStatus[] = [
      'END',
      'FULL',
      'HALF',
      'INPLAY_FIRST_HALF',
      'INPLAY_SECOND_HALF',
      'PENALTY_SHOOTOUT',
      'PRE_MATCH'
    ];

    return typeof status === 'string' && validStatuses.includes(status as FootballPeriodStatus);
  };

  const isValidFootballDurationPeriod = (status: unknown): status is FootballPeriod | undefined => {
    const validStatuses: (FootballPeriod | undefined)[] = ['REGULAR', 'EXTRA', undefined];

    return validStatuses.includes(status as FootballPeriod | undefined);
  };

  if (
    !footballData ||
    !footballData.incidents ||
    !footballData.duration ||
    !footballData.duration.period ||
    !isValidFootballDurationPeriod(footballData.duration.period) ||
    !isValidFootballDurationStatus(footballData.duration.status)
  ) {
    return null;
  }

  const isExtraTime = footballData.duration.period === 'EXTRA';

  return (
    <div className={styles.matchIncidents}>
      {/* First Half */}
      <div
        className={classNames(styles.matchIncidentsColumn, {
          [styles.extraTimeView]: isExtraTime
        })}
      >
        <TimelineEvents
          currentTimeInMinutes={firstHalf.currentTimeInMinutes || 0}
          timelineEvents={firstHalf.timelineEvents}
          totalMinutes={45}
        />
      </div>

      {/* Second Half */}
      <div
        className={classNames(styles.matchIncidentsColumn, {
          [styles.extraTimeView]: isExtraTime
        })}
      >
        <TimelineEvents
          currentTimeInMinutes={secondHalf.currentTimeInMinutes || 0}
          timelineEvents={secondHalf.timelineEvents}
          totalMinutes={45}
        />
      </div>

      {/* Extra Time */}
      {isExtraTime ? (
        <>
          {/* First Half Extra Time */}
          <div className={styles.matchIncidentsColumn}>
            <TimelineEvents
              currentTimeInMinutes={firstHalf.currentExtraTimeInMinutes || 0}
              timelineEvents={firstHalf.extraTimelineEvents}
              totalMinutes={15}
            />
          </div>

          {/* Second Half Extra Time */}
          <div className={styles.matchIncidentsColumn}>
            <TimelineEvents
              currentTimeInMinutes={secondHalf.currentExtraTimeInMinutes || 0}
              timelineEvents={secondHalf.extraTimelineEvents}
              totalMinutes={15}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
